import { useAuth } from './AuthContext';

/**
 * Whether the current user may see or edit model-provider credentials.
 *
 * Mirrors the backend ``app.auth.provider_access.can_manage_providers``:
 * self-hosted installs manage their own keys (workspace role decides), while
 * on SaaS every key is SpecificAI's, so only the ``god`` platform role keeps
 * the Model Providers tab, its deep links, and the "add a key" CTAs. The
 * auth-disabled bypass mirrors ``require_god``. The backend enforces the
 * same rule (403 + redacted settings); this only hides the UI.
 */
export function canManageProviders(auth) {
    if (auth?.isSaas !== true) return true;
    if (auth?.isAuthEnabled === false) return true;
    return auth?.platformRole === 'god';
}

export function useCanManageProviders() {
    return canManageProviders(useAuth());
}

/**
 * Whether a model picker may offer "Can't find it? Add a new key".
 *
 * Never on SaaS: every key there is SpecificAI's and the picker lists only
 * the models the platform verified, so the shortcut has nothing to add even
 * for a god (who still manages keys from Settings). Elsewhere it follows
 * `canManageProviders`.
 */
export function canAddKeyFromModelPicker(auth) {
    if (auth?.isSaas === true) return false;
    return canManageProviders(auth);
}

export function useCanAddKeyFromModelPicker() {
    return canAddKeyFromModelPicker(useAuth());
}

/**
 * Whether the current user may see or edit the other platform-owned Settings
 * tabs: hardware device, external deploy targets, and the personal Hugging
 * Face / Tavily tokens.
 *
 * Mirrors ``app.auth.provider_access.can_manage_platform_settings`` — the
 * same policy as model providers (anyone off SaaS, gods only on SaaS), kept
 * as its own name so the two gates can diverge later without a hunt. The
 * backend 403s the write routes and redacts the read; this only hides UI.
 */
export function canManagePlatformSettings(auth) {
    return canManageProviders(auth);
}

export function useCanManagePlatformSettings() {
    return canManagePlatformSettings(useAuth());
}

/**
 * Copy for "no provider" banners when the user cannot add credentials
 * themselves (SaaS non-god): the keys are SpecificAI's, so the only remedy
 * is to contact us.
 */
export const PROVIDERS_MANAGED_BY_PLATFORM_HINT =
    'Model providers are managed by SpecificAI on this platform. Contact us to enable it';
